import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { IdpType, IdpUpdate, Tenant } from '@/types';
import { getIdpProvider, IDP_TYPES } from '@/types';
import { Check, Upload, Image as ImageIcon, Trash2, ArrowLeft, Settings, Globe, Shield, ShieldOff, Plus } from 'lucide-react';
import { useIdp } from '@/store/idp';
import { useTenants } from '@/store/tenant';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { TabBar } from '@/components/ui/TabBar';
import { InfoGrid } from '@/components/ui/InfoGrid';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

export const ManageIdpPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const idpStore = useIdp(id);
    const allTenants = useTenants((state) => state.tenants);
    const listAllTenants = useTenants((state) => state.list);

    const tabs = [
        { id: 'details', label: 'Details', icon: Settings },
        { id: 'tenants', label: 'Tenants', icon: Globe },
    ];
    const [activeTab, setActiveTab] = useState<string>('details');

    const [displayName, setDisplayName] = useState<string>('');
    const [provider, setProvider] = useState<IdpType>('okta');
    const [logo, setLogo] = useState<string>('');
    const [enabled, setEnabled] = useState<boolean>(true);
    const [availableTypes, setAvailableTypes] = useState<IdpType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [tenantList, setTenantList] = useState<Tenant[]>([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'assignTenant' | null>(null);
    const [modalValue, setModalValue] = useState('');
    const [isSavingModal, setIsSavingModal] = useState(false);

    const [confirmText, setConfirmText] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([idpStore.get(), idpStore.listTypes(), idpStore.tenants.list()])
            .then(([data, types, tenants]) => {
                setDisplayName(data.display_name);
                setProvider(data.provider as IdpType);
                setLogo(data.logo_url || '');
                setEnabled(data.enabled);
                setAvailableTypes(types.filter(t => IDP_TYPES.includes(t as any)));
                setTenantList(tenants as unknown as Tenant[]);
                setBreadcrumbs([
                    { name: 'IdPs', to: '/idps' },
                    { name: data.display_name || 'Manage' }
                ]);
            })
            .catch((e) => {
                console.error(e);
                toast.error('Failed to load identity provider');
                navigate('/m/idps');
            })
            .finally(() => setLoading(false));
    }, [id, idpStore, setBreadcrumbs, navigate]);

    useEffect(() => {
        if (modalOpen && modalType === 'assignTenant') {
            listAllTenants();
        }
    }, [modalOpen, modalType, listAllTenants]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setIsSubmitting(true);
        const payload: IdpUpdate = {
            provider,
            display_name: displayName,
            logo_url: logo || undefined,
            status: enabled ? 1 : 0,
        };
        try {
            await idpStore.update(payload);
            toast.success('Identity provider updated successfully');
        } catch {
            toast.error('Failed to update identity provider');
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDeletion = async () => {
        if (!id || confirmText !== displayName) return;
        setIsDeleting(true);
        try {
            await idpStore.delete();
            toast.success('Identity provider deleted successfully');
            navigate('/m/idps');
        } catch {
            toast.error('Failed to delete identity provider');
            setShowDeleteConfirm(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const removeTenant = async (tenantId: string) => {
        try {
            await idpStore.tenants.unassign(tenantId);
            setTenantList((prev) => prev.filter((t) => t.id !== tenantId));
            toast.success('Tenant removed');
        } catch {
            toast.error('Failed to remove tenant');
        }
    };

    const assignTenant = async (tenantId: string) => {
        try {
            await idpStore.tenants.assign(tenantId);
            const tenant = allTenants?.find((t) => t.id === tenantId);
            if (tenant) setTenantList((prev) => [...prev, tenant]);
            toast.success('Tenant assigned');
        } catch {
            toast.error('Failed to assign tenant');
        }
    };

    const nonAssignedTenants = allTenants?.filter((t) => !tenantList.some((et) => et.id === t.id));

    if (loading) return <div className="h-full flex items-center justify-center"><Loader /></div>;

    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title={displayName}
                subtitle={`Managing identity provider ID: ${id}`}
                actions={
                    <div className="flex gap-3">
                        <Button variant="ghost" onClick={() => navigate('/m/idps')} className="gap-2">
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </Button>
                    </div>
                }
            />

            <div className="mt-8">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    className="mb-8"
                />

                <div className="grid gap-6">
                    {activeTab === 'details' && (
                        <div className="max-w-4xl animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Configuration Settings"
                                subtitle="General information and handshake protocol configurations."
                                className="glass"
                            >
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <InfoGrid cols={2}>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Provider</label>
                                            <div className="flex gap-4 items-center">
                                                <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden border border-slate-200/60 dark:border-slate-700/60 shadow bg-white dark:bg-slate-800 shrink-0">
                                                    {getIdpProvider(provider)?.logoUrl ? (
                                                        <img src={getIdpProvider(provider)?.logoUrl} alt={provider} className="w-8 h-8 object-contain" />
                                                    ) : (
                                                        <Shield className="w-6 h-6 text-slate-400" />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <select
                                                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition-all font-bold text-slate-900 dark:text-white"
                                                        value={provider}
                                                        onChange={(e) => setProvider(e.target.value as IdpType)}
                                                    >
                                                        {availableTypes.map((t) => (
                                                            <option key={t} value={t}>{getIdpProvider(t)?.name || t}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-slate-500 font-medium">Standard identity handshake type.</p>
                                        </div>

                                        <InputField
                                            label="Display Name"
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                            required
                                            icon={<Shield className="w-4 h-4" />}
                                        />

                                        <div className="md:col-span-2 space-y-3">
                                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Branding Logo</label>
                                            <div className="flex flex-col sm:flex-row items-stretch gap-4">
                                                <label className="flex-1 flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-900 hover:border-orange-500/30 transition-all cursor-pointer group">
                                                    <div className="flex flex-col items-center text-center">
                                                        <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-2 group-hover:bg-orange-100 dark:group-hover:bg-orange-500/20 group-hover:text-orange-600 transition-colors">
                                                            <Upload className="w-5 h-5" />
                                                        </div>
                                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Update Logo</p>
                                                        <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest font-bold">Swap Image</p>
                                                    </div>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                const reader = new FileReader();
                                                                reader.onload = () => setLogo(reader.result as string);
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                    />
                                                </label>

                                                <div className="w-40 h-40 shrink-0 border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-950 flex items-center justify-center overflow-hidden p-2 shadow-sm">
                                                    {logo ? (
                                                        <img src={logo} alt="IDP Logo" className="max-w-full max-h-full object-contain" />
                                                    ) : (
                                                        <div className="text-slate-300 flex flex-col items-center gap-2">
                                                            <ImageIcon className="w-8 h-8 opacity-20" />
                                                            <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">Empty</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </InfoGrid>

                                    <div className="bg-slate-50/50 dark:bg-slate-900/30 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900 dark:text-white">Active Status</span>
                                                <span className="text-[11px] text-slate-500">Allow SSO requests via this configuration.</span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setEnabled(!enabled)}
                                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ring-offset-2 focus:ring-2 focus:ring-orange-500 ${enabled ? 'bg-orange-500' : 'bg-slate-200 dark:bg-slate-700'
                                                }`}
                                        >
                                            <span
                                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>

                                    <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                        <Button
                                            type="submit"
                                            isLoading={isSubmitting}
                                            className="min-w-40 gap-2"
                                        >
                                            <Check className="w-4 h-4" />
                                            Save Changes
                                        </Button>
                                    </div>
                                </form>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'tenants' && (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <Card
                                title="Connected Tenants"
                                subtitle="Organizations utilizing this identity provider for authentication."
                                noPadding
                                className="glass"
                                actions={
                                    <Button
                                        size="sm"
                                        onClick={() => { setModalType('assignTenant'); setModalValue(''); setModalOpen(true); }}
                                        className="gap-2"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Assign Tenant
                                    </Button>
                                }
                            >
                                {!!!tenantList?.length ? (
                                    <div className="p-16 text-center text-slate-500 italic">
                                        No tenants are currently linked to this provider.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                                    <th className="px-6 py-3">Tenant Name</th>
                                                    <th className="px-6 py-3">Unique Slug</th>
                                                    <th className="px-6 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                                {tenantList.map((t) => (
                                                    <tr key={t.id} className="hover:bg-orange-50/50 dark:hover:bg-orange-500/5 transition-colors group">
                                                        <td className="px-6 py-3">
                                                            <div className="flex items-center gap-3">
                                                                {t.logo_url ? (
                                                                    <div className="w-9 h-9 rounded-lg border border-slate-200/60 bg-white flex items-center justify-center overflow-hidden shrink-0">
                                                                        <img src={t.logo_url} alt={t.name} className="w-6 h-6 object-contain" />
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold text-sm">
                                                                        {t.name?.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{t.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className="text-xs font-mono text-slate-500 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">{t.slug}</span>
                                                        </td>
                                                        <td className="px-6 py-3 text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeTenant(t.id)}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                                                            >
                                                                <ShieldOff className="w-4 h-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {modalOpen && modalType === 'assignTenant' && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card
                        title="Assign Tenant"
                        className="w-full max-w-md shadow-2xl border-none"
                    >
                        <div className="py-4 space-y-4">
                            <p className="text-sm text-slate-500">Select a tenant to enable this provider for.</p>
                            <SearchableSelect
                                placeholder="Select tenant..."
                                value={modalValue}
                                onChange={setModalValue}
                                options={(nonAssignedTenants ?? []).map((t) => ({
                                    value: t.id,
                                    label: t.name,
                                    sublabel: t.slug,
                                    logo: t.logo_url || undefined,
                                    icon: t.logo_url ? undefined : (
                                        <div className="w-5 h-5 rounded-md bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold text-[10px]">
                                            {t.name?.charAt(0)}
                                        </div>
                                    ),
                                }))}
                            />
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
                            <Button
                                isLoading={isSavingModal}
                                disabled={!modalValue}
                                onClick={async () => {
                                    setIsSavingModal(true);
                                    try {
                                        await assignTenant(modalValue);
                                        setModalOpen(false);
                                    } finally {
                                        setIsSavingModal(false);
                                    }
                                }}
                            >
                                Assign
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Deletion confirmation modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card className="w-full max-w-md shadow-2xl border-red-200 dark:border-red-900/30">
                        <div className="flex flex-col items-center text-center py-4">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-500/10 text-red-600 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-8 h-8" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Delete Provider</h2>
                            <p className="text-sm text-slate-500 mt-2 px-4 italic leading-relaxed">
                                Warning: Deleting <span className="font-bold text-red-600 dark:text-red-400">"{displayName}"</span> is permanent.
                                All SSO integrations for linked tenants will immediately fail.
                            </p>

                            <div className="w-full mt-8 text-left space-y-3">
                                <InputField
                                    label={`Type "${displayName}" to confirm`}
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={displayName}
                                />
                            </div>

                            <div className="w-full flex gap-3 mt-10">
                                <Button variant="ghost" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                                <Button
                                    variant="danger"
                                    className="flex-1"
                                    disabled={confirmText !== displayName || isDeleting}
                                    isLoading={isDeleting}
                                    onClick={confirmDeletion}
                                >
                                    Confirm Terminate
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};